import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  CircularProgress,
} from '@mui/material';
import Header from '../../shared/header/Header';
import StarIcon from '@mui/icons-material/Star';
import DownloadIcon from '@mui/icons-material/Download';
import { useGetAgentsListQuery, useGetAnalyticsStatsQuery, useGetTopAgentsQuery } from '../../data/api/endPointsSlice';
import { ApiError, showSnackbarErrorMessage } from '../../shared/utiles/Utiles';
import { UseLeaderboardStatistics } from '../../shared/customHooks/useLeaderboardStatistics/UseLeaderboardStatistics';
import { LogTabEventToGA } from '../../shared/googleAnalytics/GoogleAnalytics';
import {
  topPublishersTabClickedText,
  topDownloadedAgentsTabClickedText,
  topRatedAgentsTabClickedText,
  domainTabClickedText,
  rolesSupportedTabClickedText,
  cloudProvidersTabClickedText,
} from '../../shared/googleAnalytics/GoogleAnalyticsConstants';

enum AgentInsights {
  TopPublishers = 'Top Publishers',
  TopDownloadedAgents = 'Top Downloaded Agents',
  TopRatedAgents = 'Top Rated Agents',
  TopEnquired = 'Top Enquired',
  TopStarted = 'Top Started',
  TopAccessed = 'Top Accessed',
  Domain = 'Domain',
  RolesSupported = 'Roles Supported',
  CloudProviders = 'Cloud Providers',
}

const Leaderboard: React.FC = () => {
  const [agentInsightsActiveTab, setAgentInsightsActiveTab] = useState<string>(AgentInsights.TopPublishers);

  // Fetch agents data
  const {
    data: agentList = [],
    isLoading: agentListLoading,
    error: agentListError,
    refetch: refetchAgentList,
  } = useGetAgentsListQuery();

  // Fetch analytics stats
  const {
    data: analyticsStats,
  } = useGetAnalyticsStatsQuery();

  // Fetch top agents
  const {
    data: topAgents,
    isLoading: topAgentsLoading,
  } = useGetTopAgentsQuery();

  useEffect(() => {
    if (agentListError) {
      showSnackbarErrorMessage(agentListError as ApiError);
    }
    refetchAgentList();
  }, [agentListError, refetchAgentList]);

  // Use the custom hook to get all statistics
  const {
    summaryStats,
    domainBreakdownList,
    rolesSupportedList,
    cloudProvidersList,
    topRatedAgents,
    topDownloadedAgents,
    topPublishers,
  } = UseLeaderboardStatistics(agentList);

  // Agent Insights
  const agentInsightsNavItems = [
    AgentInsights.TopPublishers,
    AgentInsights.TopDownloadedAgents,
    AgentInsights.TopRatedAgents,
    AgentInsights.TopEnquired,
    AgentInsights.TopStarted,
    AgentInsights.TopAccessed,
    AgentInsights.Domain,
    AgentInsights.RolesSupported,
    AgentInsights.CloudProviders,
  ];

  const handleAgentInsightTabClick = (agentInsight: string) => {
    // Log the tab click event to Google Analytics
    switch (agentInsight) {
      case AgentInsights.TopPublishers:
        LogTabEventToGA(topPublishersTabClickedText);
        break;
      case AgentInsights.TopDownloadedAgents:
        LogTabEventToGA(topDownloadedAgentsTabClickedText);
        break;
      case AgentInsights.TopRatedAgents:
        LogTabEventToGA(topRatedAgentsTabClickedText);
        break;
      case AgentInsights.Domain:
        LogTabEventToGA(domainTabClickedText);
        break;
      case AgentInsights.RolesSupported:
        LogTabEventToGA(rolesSupportedTabClickedText);
        break;
      case AgentInsights.CloudProviders:
        LogTabEventToGA(cloudProvidersTabClickedText);
        break;
    }

    setAgentInsightsActiveTab(agentInsight);
  };

  const renderAgentInsightsContent = () => {
    switch (agentInsightsActiveTab) {
      case AgentInsights.Domain:
        return (
          <>
            <Typography variant="h6" fontWeight={'bold'} color="primary" sx={{ mb: 2 }}>
              Domain Breakdown
            </Typography>

            {domainBreakdownList.length > 0 ? (
              domainBreakdownList.map((item, index) => (
                <Box key={index} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>{item.domain}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography fontWeight="bold" sx={{ minWidth: '50px', textAlign: 'right' }}>
                        {item.count.toLocaleString()}
                      </Typography>
                      <Typography color="text.secondary" sx={{ minWidth: '70px', textAlign: 'right' }}>
                        {item.percentage}%
                      </Typography>
                    </Box>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={item.percentage}
                    sx={{
                      height: 8,
                      borderRadius: 5,
                      backgroundColor: '#f0f0f0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: '#1976d2',
                      },
                    }}
                  />
                </Box>
              ))
            ) : (
              <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #eaeaea', textAlign: 'center' }}>
                <Typography color="text.secondary">No domain data available</Typography>
              </Paper>
            )}
          </>
        );

      case AgentInsights.RolesSupported:
        return (
          <>
            <Typography variant="h6" fontWeight={'bold'} color="primary" sx={{ mb: 2 }}>
              Roles Breakdown
            </Typography>
            {rolesSupportedList.length > 0 ? (
              rolesSupportedList.map((item, index) => (
                <Box key={index} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>{item.role}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography fontWeight="bold" sx={{ minWidth: '50px', textAlign: 'right' }}>
                        {item.count}
                      </Typography>
                      <Typography color="text.secondary" sx={{ minWidth: '70px', textAlign: 'right' }}>
                        {item.percentage}%
                      </Typography>
                    </Box>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={item.percentage}
                    sx={{
                      height: 8,
                      borderRadius: 5,
                      backgroundColor: '#f0f0f0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: '#1976d2',
                      },
                    }}
                  />
                </Box>
              ))
            ) : (
              <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #eaeaea', textAlign: 'center' }}>
                <Typography color="text.secondary">No roles data available</Typography>
              </Paper>
            )}
          </>
        );

      case AgentInsights.CloudProviders:
        return (
          <>
            <Typography variant="h6" fontWeight={'bold'} color="primary" sx={{ mb: 2 }}>
              Cloud Providers Breakdown
            </Typography>
            {cloudProvidersList.length > 0 ? (
              cloudProvidersList.map((item, index) => (
                <Box key={index} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>{item.cloudProvider}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography fontWeight="bold" sx={{ minWidth: '50px', textAlign: 'right' }}>
                        {item.count}
                      </Typography>
                      <Typography color="text.secondary" sx={{ minWidth: '70px', textAlign: 'right' }}>
                        {item.percentage}%
                      </Typography>
                    </Box>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={item.percentage}
                    sx={{
                      height: 8,
                      borderRadius: 5,
                      backgroundColor: '#f0f0f0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: '#1976d2',
                      },
                    }}
                  />
                </Box>
              ))
            ) : (
              <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #eaeaea', textAlign: 'center' }}>
                <Typography color="text.secondary">No cloud providers data available</Typography>
              </Paper>
            )}
          </>
        );

      case AgentInsights.TopRatedAgents:
        return (
          <>
            <Typography variant="h6" fontWeight={'bold'} color="primary" sx={{ mb: 2 }}>
              Top Rated Agents
            </Typography>
            <Stack spacing={2}>
              {topRatedAgents.length > 0 ? (
                topRatedAgents.map((agent, index) => (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid #eaeaea',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="h6" fontSize={'1rem'} fontWeight={'bold'}>
                          {agent.name}
                        </Typography>
                        <Typography color="text.secondary">by {agent.author}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: '55px' }}>
                          <StarIcon sx={{ color: '#FFD700', mr: 0.5 }} />
                          <Typography fontWeight="bold">{agent.rating}</Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                ))
              ) : (
                <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #eaeaea', textAlign: 'center' }}>
                  <Typography color="text.secondary">No rated agents available</Typography>
                </Paper>
              )}
            </Stack>
          </>
        );

      case AgentInsights.TopDownloadedAgents:
        return (
          <>
            <Typography variant="h6" fontWeight={'bold'} color="primary" sx={{ mb: 2 }}>
              Top Downloaded Agents
            </Typography>
            <Stack spacing={2}>
              {topDownloadedAgents.length > 0 ? (
                topDownloadedAgents.map((agent, index) => (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid #eaeaea',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="h6" fontSize={'1rem'} fontWeight={'bold'}>
                          {agent.name}
                        </Typography>
                        <Typography color="text.secondary">by {agent.author}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <DownloadIcon sx={{ color: '#1976d2', mr: 0.5 }} />
                          <Typography fontWeight="bold">{agent.downloads.toLocaleString()}</Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                ))
              ) : (
                <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #eaeaea', textAlign: 'center' }}>
                  <Typography color="text.secondary">No download data available</Typography>
                </Paper>
              )}
            </Stack>
          </>
        );

      case AgentInsights.TopEnquired:
        return (
          <>
            <Typography variant="h6" fontWeight={'bold'} color="primary" sx={{ mb: 2 }}>
              Top Enquired Agents
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Top 5 agents by "More Details" clicks
            </Typography>
            <Stack spacing={2}>
              {topAgentsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : topAgents?.top_enquired && topAgents.top_enquired.length > 0 ? (
                topAgents.top_enquired.map((agent, index) => (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid #eaeaea',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body1" fontWeight={'bold'}>
                        {agent.name}
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="primary">
                        {agent.count}
                      </Typography>
                    </Box>
                  </Paper>
                ))
              ) : (
                <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #eaeaea', textAlign: 'center' }}>
                  <Typography color="text.secondary">No data available</Typography>
                </Paper>
              )}
            </Stack>
          </>
        );

      case AgentInsights.TopStarted:
        return (
          <>
            <Typography variant="h6" fontWeight={'bold'} color="primary" sx={{ mb: 2 }}>
              Top Started Agents
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Top 5 agents by "Start Agent" clicks
            </Typography>
            <Stack spacing={2}>
              {topAgentsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : topAgents?.top_started && topAgents.top_started.length > 0 ? (
                topAgents.top_started.map((agent, index) => (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid #eaeaea',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body1" fontWeight={'bold'}>
                        {agent.name}
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="primary">
                        {agent.count}
                      </Typography>
                    </Box>
                  </Paper>
                ))
              ) : (
                <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #eaeaea', textAlign: 'center' }}>
                  <Typography color="text.secondary">No data available</Typography>
                </Paper>
              )}
            </Stack>
          </>
        );

      case AgentInsights.TopAccessed:
        return (
          <>
            <Typography variant="h6" fontWeight={'bold'} color="primary" sx={{ mb: 2 }}>
              Top Accessed Agents
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Top 5 agents by "Open Chat" clicks
            </Typography>
            <Stack spacing={2}>
              {topAgentsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : topAgents?.top_accessed && topAgents.top_accessed.length > 0 ? (
                topAgents.top_accessed.map((agent, index) => (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid #eaeaea',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body1" fontWeight={'bold'}>
                        {agent.name}
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="primary">
                        {agent.count}
                      </Typography>
                    </Box>
                  </Paper>
                ))
              ) : (
                <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #eaeaea', textAlign: 'center' }}>
                  <Typography color="text.secondary">No data available</Typography>
                </Paper>
              )}
            </Stack>
          </>
        );

      case AgentInsights.TopPublishers:
        return (
          <>
            <Typography variant="h6" fontWeight={'bold'} color="primary" sx={{ mb: 2 }}>
              Top Publishers
            </Typography>
            <Stack spacing={2}>
              {topPublishers.length > 0 ? (
                topPublishers.map((publisher, index) => (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid #eaeaea',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="h6" fontSize={'1rem'} fontWeight={'bold'}>
                          {publisher.name}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography fontWeight="bold">{publisher.agentsCount}</Typography>
                        <Typography color="text.secondary" sx={{ ml: 1 }}>
                          agents
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                ))
              ) : (
                <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #eaeaea', textAlign: 'center' }}>
                  <Typography color="text.secondary">No publisher data available</Typography>
                </Paper>
              )}
            </Stack>
          </>
        );

      default:
        return <Typography>Select a category from the left menu</Typography>;
    }
  };

  return (
    <>
      <Header />

      <Container maxWidth="lg" sx={{ mt: 10, mb: 4 }}>
        {agentListLoading ? (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress />
            <Typography variant="h5">Loading Statistics</Typography>
          </Box>
        ) : (
          <>
            <Typography variant="h4" fontWeight="bold">
              Marketplace Statistics
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 5 }}>
              Discover real-time trends and the growth of our thriving AI agent marketplace community on our live
              Statistics page.
            </Typography>
            {/* Summary Statistics Cards - First Row */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
              {summaryStats.map((stat, index) => (
                <Box key={index} sx={{ flex: '1 1 200px', minWidth: { xs: '100%', sm: '45%', md: '30%' } }}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 2,
                      border: '1px solid #eaeaea',
                    }}
                  >
                    <Typography color="primary" fontWeight="medium" sx={{ mb: 1 }}>
                      {stat.title}
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" fontSize={'1.5rem'}>
                      {stat.value}
                    </Typography>
                  </Paper>
                </Box>
              ))}
            </Box>
            
            {/* Analytics Stats - Second Row */}
            {analyticsStats && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 6 }}>
                <Box sx={{ flex: '1 1 200px', minWidth: { xs: '100%', sm: '45%', md: '30%' } }}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 2,
                      border: '1px solid #eaeaea',
                    }}
                  >
                    <Typography color="primary" fontWeight="medium" sx={{ mb: 1 }}>
                      Unique Users
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" fontSize={'1.5rem'}>
                      {analyticsStats.unique_users_count}
                    </Typography>
                  </Paper>
                </Box>
                
                <Box sx={{ flex: '1 1 200px', minWidth: { xs: '100%', sm: '45%', md: '30%' } }}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 2,
                      border: '1px solid #eaeaea',
                    }}
                  >
                    <Typography color="primary" fontWeight="medium" sx={{ mb: 1 }}>
                      Total Executed Agents
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" fontSize={'1.5rem'}>
                      {analyticsStats.total_accessed_agents}
                    </Typography>
                  </Paper>
                </Box>
                
                <Box sx={{ flex: '1 1 200px', minWidth: { xs: '100%', sm: '45%', md: '30%' } }}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 2,
                      border: '1px solid #eaeaea',
                    }}
                  >
                    <Typography color="primary" fontWeight="medium" sx={{ mb: 1 }}>
                      Total Visits
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" fontSize={'1.5rem'}>
                      {analyticsStats.total_visits}
                    </Typography>
                  </Paper>
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 5 }} />

            {/* Main Content with Left Navigation */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
              {/* Left Navigation */}
              <Box sx={{ width: { xs: '100%', md: '25%' }, mt: 3 }}>
                <List sx={{ p: 0 }}>
                  <ListItem>
                    <Typography variant="h6" color="primary" fontWeight={'bold'}>
                      Insights
                    </Typography>
                  </ListItem>
                  {agentInsightsNavItems.map((agentInsight, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        mb: 1,
                        backgroundColor: agentInsightsActiveTab === agentInsight ? '#f0f0f0' : 'transparent',
                        borderRadius: 2,
                        '&:hover': {
                          backgroundColor: '#f0f7ff',
                          cursor: 'pointer',
                        },
                      }}
                      onClick={() => handleAgentInsightTabClick(agentInsight)}
                    >
                      <ListItemText
                        primary={agentInsight}
                        sx={{ color: agentInsightsActiveTab === agentInsight ? 'default' : 'text.secondary' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>

              {/* Right Content */}
              <Box sx={{ width: { xs: '100%', md: '75%' } }}>
                <Paper
                  elevation={1}
                  sx={{
                    p: 4,
                    borderRadius: 2,
                    border: '1px solid #eaeaea',
                    maxHeight: '450px',
                    overflow: 'auto',
                  }}
                >
                  {renderAgentInsightsContent()}
                </Paper>
              </Box>
            </Box>
          </>
        )}
      </Container>
    </>
  );
};

export default Leaderboard;
